'use client';

import { useState, useCallback, useSyncExternalStore } from 'react';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp?: string;
  version?: number;
}

export const COOKIE_CONSENT_KEY = 'dr_cookie_consent_v1';

export const OPEN_COOKIE_SETTINGS_EVENT = 'dr-open-cookie-settings';

export const COOKIE_CONSENT_UPDATED_EVENT = 'dr-cookie-consent-updated';

export const CURRENT_CONSENT_VERSION = 1;

export const DEFAULT_COOKIE_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

export const ALL_ACCEPTED_COOKIE_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: true,
  marketing: true,
};

export const ESSENTIAL_ONLY_COOKIE_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

export function getStoredCookiePreferences(): CookiePreferences | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);

    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookiePreferences;

    if (parsed.version !== CURRENT_CONSENT_VERSION) return null;

    return parsed;
  } catch {
    return null;
  }
}

export function saveCookiePreferences(preferences: Partial<CookiePreferences>) {
  if (typeof window === 'undefined') return;
  const fullPreferences: CookiePreferences = {
    necessary: true,
    analytics: Boolean(preferences.analytics),
    marketing: Boolean(preferences.marketing),
    timestamp: new Date().toISOString(),
    version: CURRENT_CONSENT_VERSION,
  };

  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(fullPreferences));
    window.dispatchEvent(
      new CustomEvent(COOKIE_CONSENT_UPDATED_EVENT, {
        detail: fullPreferences,
      }),
    );
  } catch (err) {
    console.error('Failed to save cookie preferences:', err);
  }
}

export function openCookieSettings() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
}

const noopSubscribe = () => undefined;

function subscribeCookieStore(callback: () => void) {
  if (typeof window === 'undefined') return noopSubscribe;

  window.addEventListener(COOKIE_CONSENT_UPDATED_EVENT, callback);
  window.addEventListener('storage', callback);

  return () => {
    window.removeEventListener(COOKIE_CONSENT_UPDATED_EVENT, callback);
    window.removeEventListener('storage', callback);
  };
}

function getCookieSnapshot(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(COOKIE_CONSENT_KEY);
  } catch {
    return null;
  }
}

function getServerCookieSnapshot(): string | null {
  return null;
}

export function useCookieConsent() {
  const rawConsent = useSyncExternalStore(
    subscribeCookieStore,
    getCookieSnapshot,
    getServerCookieSnapshot,
  );

  const [isManuallyOpened, setIsManuallyOpened] = useState(false);

  const [isHydrated, setIsHydrated] = useState(false);

  const handleManualOpen = useCallback(() => {
    setIsManuallyOpened(true);
  }, []);

  const handleSubscribeManual = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (!isHydrated) {
      setIsHydrated(true);
    }
  }, [isHydrated]);

  useState(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleManualOpen);
    }
  });

  const parsedPreferences: CookiePreferences | null = rawConsent
    ? (() => {
        try {
          const parsed = JSON.parse(rawConsent) as CookiePreferences;

          return parsed.version === CURRENT_CONSENT_VERSION ? parsed : null;
        } catch {
          return null;
        }
      })()
    : null;

  const isConsentGiven = parsedPreferences !== null;
  const isBannerVisible = isHydrated && (!isConsentGiven || isManuallyOpened);

  const acceptAll = useCallback(() => {
    saveCookiePreferences(ALL_ACCEPTED_COOKIE_PREFERENCES);
    setIsManuallyOpened(false);
  }, []);

  const acceptEssentialOnly = useCallback(() => {
    saveCookiePreferences(ESSENTIAL_ONLY_COOKIE_PREFERENCES);
    setIsManuallyOpened(false);
  }, []);

  const saveCustom = useCallback((custom: Partial<CookiePreferences>) => {
    saveCookiePreferences(custom);
    setIsManuallyOpened(false);
  }, []);

  const closeBanner = useCallback(() => {
    setIsManuallyOpened(false);
  }, []);

  return {
    preferences: parsedPreferences || DEFAULT_COOKIE_PREFERENCES,
    hasGivenConsent: isConsentGiven,
    isOpen: isBannerVisible,
    isLoaded: isHydrated,
    acceptAll,
    acceptEssentialOnly,
    saveCustom,
    openSettings: () => setIsManuallyOpened(true),
    closeBanner,
    onMounted: handleSubscribeManual,
  };
}
